<script setup lang="ts">
import { reactive, ref } from 'vue';
import { ArrowLeft, ArrowRight, MailCheck } from '@lucide/vue';
import { richiestaResetPasswordSchema } from '@evolfin/shared';
import { http, EvolfinApiError } from '@/api/client';
import { useAltezzaAnimata } from '@/composables/altezzaAnimata';
import MarchioEvolfin from '@/components/app/MarchioEvolfin.vue';
import SfondoAmbiente from '@/components/app/SfondoAmbiente.vue';
import AvvisoErrore from '@/components/ui/AvvisoErrore.vue';
import CampoTesto from '@/components/ui/CampoTesto.vue';
import PulsanteAzione from '@/components/ui/PulsanteAzione.vue';

definePage({
  meta: { pubblica: true, titolo: 'Password dimenticata' },
});

const form = reactive({ email: '' });
const erroriCampo = ref<Record<string, string[]>>({});
const erroreGenerale = ref<string | null>(null);
const inviata = ref(false);
const caricamento = ref(false);

const { riferimentoContenuto, altezza, animando } = useAltezzaAnimata();

const primoErrore = (chiave: string) => erroriCampo.value[chiave]?.[0];

async function invia(): Promise<void> {
  erroriCampo.value = {};
  erroreGenerale.value = null;

  const esito = richiestaResetPasswordSchema.safeParse({ email: form.email });
  if (!esito.success) {
    for (const problema of esito.error.issues) {
      const chiave = problema.path.join('.');
      (erroriCampo.value[chiave] ??= []).push(problema.message);
    }
    return;
  }

  caricamento.value = true;
  try {
    await http.post('/api/auth/reset-password/richiesta', esito.data);
    // Il server risponde sempre 204, esista o no l'indirizzo: mostriamo lo
    // stesso messaggio in ogni caso, così la pagina non rivela chi è registrato.
    inviata.value = true;
  } catch (errore) {
    if (errore instanceof EvolfinApiError && errore.dettagli.statusCode === 429) {
      erroreGenerale.value = 'Troppe richieste ravvicinate. Attendi un minuto e riprova.';
    } else {
      erroreGenerale.value = 'Errore imprevisto. Riprova tra qualche istante.';
    }
  } finally {
    caricamento.value = false;
  }
}
</script>

<template>
  <div class="relative grid min-h-screen place-items-center overflow-hidden px-4 py-12">
    <SfondoAmbiente />

    <main class="relative w-full max-w-[400px]">
      <header class="entra mb-8 flex flex-col items-center text-center">
        <MarchioEvolfin :dimensione="60" />
        <h1 class="mt-6 font-display text-[34px] leading-[1.05] font-semibold tracking-[-0.03em]">
          Password dimenticata
        </h1>
        <p class="mt-3 max-w-[20rem] text-ink-secondary">
          Inserisci la tua email: se è registrata, ti invieremo un link per reimpostare la password.
        </p>
      </header>

      <div class="entra card card--vetro bordo-riflesso p-7" style="animation-delay: 60ms">
        <div
          class="altezza-animata"
          :class="animando && 'altezza-animata--ritaglia'"
          :style="{ height: altezza }"
        >
          <div :ref="riferimentoContenuto">
            <!-- Stato di conferma: unico esito mostrato all'utente, a prescindere
                 dall'esistenza dell'indirizzo (niente user-enumeration). -->
            <div v-if="inviata" class="flex flex-col items-center gap-3 text-center">
              <span
                class="grid size-11 place-items-center rounded-full bg-[var(--surface-accent-soft)] text-[var(--color-primary)]"
              >
                <MailCheck :size="22" :stroke-width="1.8" aria-hidden="true" />
              </span>
              <p class="text-[15px] font-medium">Controlla la posta</p>
              <p class="text-[13px] text-ink-secondary">
                Se <strong>{{ form.email }}</strong> è un account Evolfin, riceverai a breve
                un'email con le istruzioni per reimpostare la password. Il link scade tra un'ora.
              </p>
              <RouterLink
                to="/auth/login"
                class="mt-1 inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--color-primary)] hover:underline"
              >
                <ArrowLeft :size="15" :stroke-width="2" aria-hidden="true" />
                Torna all'accesso
              </RouterLink>
            </div>

            <form v-else class="flex flex-col gap-4" novalidate @submit.prevent="invia">
              <AvvisoErrore v-if="erroreGenerale" :messaggio="erroreGenerale" />

              <CampoTesto
                v-model="form.email"
                etichetta="Email"
                tipo="email"
                autocomplete="username"
                richiesto
                autofocus
                :errore="primoErrore('email')"
              />

              <PulsanteAzione tipo="submit" larghezza-piena class="mt-1" :caricamento="caricamento">
                {{ caricamento ? 'Invio in corso…' : 'Invia il link' }}
                <template #icona>
                  <ArrowRight :size="16" :stroke-width="2" aria-hidden="true" />
                </template>
              </PulsanteAzione>

              <RouterLink
                to="/auth/login"
                class="inline-flex items-center justify-center gap-1.5 text-[13px] text-ink-muted hover:text-[var(--color-primary)]"
              >
                <ArrowLeft :size="15" :stroke-width="2" aria-hidden="true" />
                Torna all'accesso
              </RouterLink>
            </form>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>
